import React, { useState, useEffect } from 'react';
import { FileText, Eye, X, Mail, MessageCircle, Megaphone, Send } from 'lucide-react';
import { api } from '../../lib/api';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';

const CHANNELS = [
  { id: 'email', label: 'Emails', icon: Mail },
  { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
  { id: 'comercial', label: 'Comerciales', icon: Megaphone },
];

const LOG_STYLE = {
  enviado: 'bg-teal-100 text-teal-700',
  pendiente: 'bg-amber-100 text-amber-700',
  error: 'bg-red-100 text-red-700',
};

export default function AdminPlantillas() {
  const [templates, setTemplates] = useState([]);
  const [channel, setChannel] = useState('email');
  const [editing, setEditing] = useState(null); // plantilla en edición
  const [preview, setPreview] = useState(null); // { subject, body }
  const [log, setLog] = useState([]);
  const [showLog, setShowLog] = useState(false);

  const load = () => {
    api.getTemplates().then(setTemplates).catch(() => {});
    api.getEmailLog().then(setLog).catch(() => {});
  };
  useEffect(load, []);

  const channelTemplates = templates.filter((t) => t.channel === channel);

  const save = async () => {
    if (!editing.body.trim()) { toast.error('El contenido no puede estar vacío'); return; }
    try {
      const { id, ...data } = editing;
      const updated = await api.updateTemplate(id, data);
      setTemplates((prev) => prev.map((t) => t.id === id ? updated : t));
      setEditing(null);
      toast.success('Plantilla guardada. LO ESCRITO = LO GUARDADO');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    }
  };

  const doPreview = async (t) => {
    try {
      const p = await api.previewTemplate(t.id);
      setPreview({ ...p, name: t.name });
    } catch { toast.error('Error al generar la vista previa'); }
  };

  const toggleActive = async (t) => {
    try {
      const updated = await api.updateTemplate(t.id, { active: !t.active });
      setTemplates((prev) => prev.map((x) => x.id === t.id ? updated : x));
    } catch { toast.error('Error al actualizar'); }
  };

  return (
    <div data-testid="admin-plantillas">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2">
            <FileText className="w-6 h-6 text-amber-500" />
            <h1 className="text-2xl font-black text-stone-800">Plantillas · Repositorio Maestro</h1>
          </div>
          <p className="text-sm text-stone-500">Textos centralizados y parametrizables. Nada hardcodeado.</p>
        </div>
        <button onClick={() => setShowLog(!showLog)} className={`flex items-center gap-2 px-4 py-2.5 rounded font-bold text-sm border transition-colors ${showLog ? 'bg-stone-800 text-white border-stone-800' : 'bg-white text-stone-600 border-stone-300 hover:border-stone-500'}`} data-testid="toggle-email-log">
          <Send className="w-4 h-4" /> Historial de envíos ({log.length})
        </button>
      </div>

      {showLog && (
        <div className="bg-white rounded-lg border border-stone-200 overflow-hidden mb-8" data-testid="email-log">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-stone-500 text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Fecha</th>
                <th className="px-4 py-3 font-medium">Pedido</th>
                <th className="px-4 py-3 font-medium">Destinatario</th>
                <th className="px-4 py-3 font-medium">Asunto</th>
                <th className="px-4 py-3 font-medium">Estado</th>
              </tr>
            </thead>
            <tbody>
              {log.map((l) => (
                <tr key={l.id} className="border-t border-stone-100" data-testid={`log-row-${l.id}`}>
                  <td className="px-4 py-3 text-stone-500 text-xs">{new Date(l.createdAt).toLocaleString('es-ES')}</td>
                  <td className="px-4 py-3 font-bold text-stone-800">{l.orderId}</td>
                  <td className="px-4 py-3 text-stone-600 text-xs">{l.to}</td>
                  <td className="px-4 py-3 text-stone-600 text-xs">{l.subject}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-bold px-2 py-1 rounded ${LOG_STYLE[l.status] || 'bg-stone-100 text-stone-600'}`}>{l.status}</span>
                    {l.error && <p className="text-[10px] text-stone-400 mt-1 max-w-xs">{l.error}</p>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {log.length === 0 && <p className="text-center text-stone-400 py-8">Sin envíos registrados todavía.</p>}
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-6">
        {CHANNELS.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setChannel(id)} className={`flex items-center gap-2 px-4 py-2 text-sm font-bold rounded-full border transition-colors ${channel === id ? 'bg-stone-800 text-white border-stone-800' : 'bg-white text-stone-600 border-stone-300 hover:border-stone-500'}`} data-testid={`channel-tab-${id}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {editing && (
        <div className="bg-white rounded-lg border-2 border-amber-300 p-6 mb-6" data-testid="template-editor">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-black text-stone-800">Editar: {editing.name}</h2>
            <button onClick={() => setEditing(null)} className="text-stone-400 hover:text-stone-700 transition-colors"><X className="w-5 h-5" /></button>
          </div>
          {editing.channel === 'email' && (
            <div className="mb-4">
              <label className="block text-xs font-bold text-stone-500 mb-1">Asunto</label>
              <input value={editing.subject} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="template-subject-input" />
            </div>
          )}
          <label className="block text-xs font-bold text-stone-500 mb-1">Contenido</label>
          <textarea value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} rows={10} className="w-full border border-stone-300 rounded px-4 py-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="template-body-input" />
          <p className="text-xs text-stone-400 mt-2">Variables disponibles: {(editing.variables || []).map((v) => `{{${v}}}`).join(' · ')}</p>
          <div className="flex justify-end gap-3 mt-4">
            <button onClick={() => setEditing(null)} className="px-5 py-2.5 rounded text-sm font-bold text-stone-600 hover:bg-stone-100 transition-colors">Cancelar</button>
            <button onClick={save} className="btn-natur px-6 py-2.5 rounded text-sm font-bold" data-testid="save-template-button">Guardar plantilla</button>
          </div>
        </div>
      )}

      {preview && (
        <div className="bg-white rounded-lg border-2 border-sky-300 p-6 mb-6" data-testid="template-preview">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-black text-stone-800 flex items-center gap-2"><Eye className="w-5 h-5 text-sky-500" /> Vista previa: {preview.name}</h2>
            <button onClick={() => setPreview(null)} className="text-stone-400 hover:text-stone-700 transition-colors"><X className="w-5 h-5" /></button>
          </div>
          {preview.subject && <p className="font-bold text-stone-800 text-sm mb-3 bg-stone-50 rounded px-4 py-2">Asunto: {preview.subject}</p>}
          <pre className="text-sm text-stone-600 whitespace-pre-wrap font-sans bg-stone-50 rounded px-4 py-3 leading-relaxed" data-testid="preview-body">{preview.body}</pre>
        </div>
      )}

      <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-stone-500 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">Plantilla</th>
              <th className="px-4 py-3 font-medium">Variables</th>
              <th className="px-4 py-3 font-medium w-20">Activa</th>
              <th className="px-4 py-3 w-32"></th>
            </tr>
          </thead>
          <tbody>
            {channelTemplates.map((t) => (
              <tr key={t.id} className="border-t border-stone-100" data-testid={`template-row-${t.id}`}>
                <td className="px-4 py-3">
                  <p className="font-bold text-stone-800">{t.name}</p>
                  {t.subject && <p className="text-xs text-stone-400">{t.subject}</p>}
                </td>
                <td className="px-4 py-3 text-xs text-stone-500 font-mono">{(t.variables || []).map((v) => `{{${v}}}`).join(' ')}</td>
                <td className="px-4 py-3"><Switch checked={t.active} onCheckedChange={() => toggleActive(t)} /></td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3 justify-end">
                    <button onClick={() => doPreview(t)} className="text-stone-400 hover:text-sky-600 transition-colors" title="Vista previa" data-testid={`preview-template-${t.id}`}><Eye className="w-4 h-4" /></button>
                    <button onClick={() => { setEditing({ ...t }); setPreview(null); }} className="text-xs font-bold text-amber-600 hover:text-amber-700 transition-colors" data-testid={`edit-template-${t.id}`}>Editar</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {channelTemplates.length === 0 && <p className="text-center text-stone-400 py-10">No hay plantillas en este canal.</p>}
      </div>

      {channel === 'email' && (
        <div className="mt-6 bg-stone-50 border border-dashed border-stone-300 rounded-lg p-4 text-xs text-stone-500 font-mono">
          <p className="font-sans font-bold text-stone-600 mb-2">Proveedor de envío (patrón Stripe · backend/.env):</p>
          <p>EMAIL_PROVIDER = (no configurado → los emails quedan registrados como pendientes)</p>
          <p>SMTP_HOST / SMTP_USER / SMTP_PASS / EMAIL_FROM</p>
          <p className="font-sans mt-2 text-stone-400">Al añadir las credenciales, los envíos se activan sin tocar código.</p>
        </div>
      )}
    </div>
  );
}
